import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';

/**
 * The width below which there is no ballot pane to be open — 600px, which is
 * ``demoTheme``'s ``md`` (`DashboardLayout`), the breakpoint the pane's own
 * ``display: { xs: 'none', md: 'block' }`` resolves against inside Toolpad's
 * provider, and where ``BallotSheet`` takes over.
 *
 * Deliberately **not** the app theme's ``down('md')`` (899.95px) that
 * ``useMediaQuery`` consumers outside that provider read. The two really do
 * differ, and this one has to follow the pane it defaults the state for:
 * raising it to 899.95 leaves 600–899 — the width a partner embed typically
 * renders at — with the pane mounted but resting closed. See #2464.
 */
const MOBILE_QUERY = '(max-width: 599.95px)';

const isMobileWidth = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(MOBILE_QUERY).matches;

/** Ballot rest state for a breakpoint: open beside the chat on desktop, closed on mobile. */
const defaultBallotOpenFor = (mobile: boolean): boolean => !mobile;

interface MobileLayoutContextType {
  ballotOpen: boolean;
  toggleMobileLayout: () => void;
  setBallotOpen: (open: boolean) => void;
}

const MobileLayoutContext = createContext<MobileLayoutContextType | undefined>(undefined);

interface MobileLayoutProviderProps {
  children: ReactNode;
}

export const MobileLayoutProvider: React.FC<MobileLayoutProviderProps> = ({ children }) => {
  const [ballotOpen, setBallotOpen] = useState(() => defaultBallotOpenFor(isMobileWidth()));

  // Re-apply the desktop default when the layout grows across the breakpoint,
  // so the ballot state can recover from a width that changed after mount. A
  // partner embed is where that bites: the iframe's width comes from the host
  // page's container, which can settle (or be laid out by an optimizer plugin)
  // after the app has already mounted and read the media query once. Measuring
  // only at mount left a desktop-width embed stuck on the mobile default —
  // chat at full width with just the ballot toggle in the corner, no panel.
  //
  // Growing into desktop only ever mounts the ballot pane, so it can never
  // pull the DOM out from under a focused control. Shrinking into mobile is
  // deliberately left alone for that reason: forcing it closed would unmount
  // a pane the user may be typing in (the guest landing's address field lives
  // there), stranding focus on ``<body>`` and discarding the partly-typed
  // address, with nothing to announce it. Mobile hides that pane by CSS
  // anyway. Resizes that stay on one side of the breakpoint keep whatever the
  // user last chose.
  const wasMobileRef = useRef(isMobileWidth());
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const apply = () => {
      const grewIntoDesktop = wasMobileRef.current && !mql.matches;
      wasMobileRef.current = mql.matches;
      if (grewIntoDesktop) setBallotOpen(true);
    };
    // The width can already have moved between the initial render and this
    // effect — reconcile once before listening.
    apply();
    mql.addEventListener('change', apply);
    return () => mql.removeEventListener('change', apply);
  }, []);

  const toggleMobileLayout = () => {
    setBallotOpen((prev) => !prev);
  };

  const value = {
    ballotOpen,
    toggleMobileLayout,
    setBallotOpen,
  };

  return <MobileLayoutContext.Provider value={value}>{children}</MobileLayoutContext.Provider>;
};

export const useMobileLayout = (): MobileLayoutContextType => {
  const context = useContext(MobileLayoutContext);
  if (context === undefined) {
    throw new Error('useMobileLayout must be used within a MobileLayoutProvider');
  }
  return context;
};

/** Returns undefined when called outside a MobileLayoutProvider (safe for optional consumers). */
export const useMobileLayoutOptional = (): MobileLayoutContextType | undefined => {
  return useContext(MobileLayoutContext);
};
